import BaseApiClient from '../BaseApiClient';
import type {
  InsightBulkPipelineRequest,
  InsightBulkPipelineResponse,
  InsightCompaniesQuery,
  InsightContentPack,
  InsightContentPackResult,
  InsightDrawerPreference,
  InsightMapFilterConfig,
  InsightSectorCard,
  InsightSegmentMenuItem
} from '@/interfaces/AudienceInsights.interface';
import type {
  InsightDrawerPayload,
  InsightHorizonPage
} from '@/components/page-components/audiences/insights/getInsightDrawer';
import type { InsightCompaniesPageData } from '@/components/page-components/audiences/insights/companies/getInsightCompanies';
import type { InsightSegment } from '@/components/page-components/audiences/insights/insightSegments';
import { getInsightsApiPath } from '../audience-manifest/insights-request';
import { audienceRequestErrorMessage } from '../audience-manifest/audienceListUtils';
import {
  clusterFromTree,
  companiesPageFromPayload,
  DRAWER_ORDER_SETTING_KEY,
  drawerFromExplorerParts,
  drawerOrderFromSettings,
  drawerOrderFromSettingValue,
  drawerOrderToSettingValue,
  horizonFromPayload,
  isPipelineUuid,
  mapFiltersFromPayload,
  overlayAreaFigures,
  pipelineResultFromPayload,
  sectorCardsFromTree,
  segmentMenuFromTree,
  segmentsFromFigures
} from './explorer-contract';

/**
 * Audience Explorer API client.
 * Calls the insights service directly with clean headers and CRM Bearer auth.
 */
function explorerPath(
  path: string,
  params: Record<string, string | undefined>
): string {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value) search.set(key, value);
  });
  const query = search.toString();
  return getInsightsApiPath(query ? `${path}?${query}` : path);
}

export const HORIZON_PAGE_SIZE = 6;
/** The explorer has no sending-company picker yet, so the user's own firm sends. */
export const DEFAULT_SENDING = 'own';

function taxonomyNode(nodeId: string | undefined): string | undefined {
  if (!nodeId || nodeId === 'all' || nodeId === 'uk' || nodeId.startsWith('region-')) {
    return undefined;
  }
  return nodeId;
}

export class ApiAudienceInsights extends BaseApiClient {
  async listSectors(): Promise<InsightSectorCard[]> {
    const response = await this.client.get(
      getInsightsApiPath('/api/explorer/figures/tree')
    );
    return sectorCardsFromTree(response.data);
  }

  async segmentMenu(): Promise<InsightSegmentMenuItem[]> {
    const response = await this.client.get(
      getInsightsApiPath('/api/explorer/figures/tree')
    );
    return segmentMenuFromTree(response.data);
  }

  async taxonomyTree() {
    const response = await this.client.get(
      getInsightsApiPath('/api/explorer/figures/tree')
    );
    return clusterFromTree(response.data);
  }

  async mapFilters(): Promise<InsightMapFilterConfig[]> {
    const response = await this.client.get(
      getInsightsApiPath('/api/explorer/map-filters')
    );
    return mapFiltersFromPayload(response.data);
  }

  async segments(): Promise<InsightSegment[]> {
    const economy = await this.client.get(getInsightsApiPath('/api/explorer/figures'));
    let customers: unknown = null;
    try {
      const response = await this.client.get(
        explorerPath('/api/explorer/figures', { customers: 'group' })
      );
      customers = response.data;
    } catch {
      customers = null;
    }
    return segmentsFromFigures(economy.data, customers);
  }

  async areaMetrics(nodeId?: string) {
    const response = await this.client.get(
      explorerPath('/api/explorer/figures/areas', {
        nodes: taxonomyNode(nodeId),
        level: 'area'
      })
    );
    return overlayAreaFigures(response.data);
  }

  async getDrawer(
    nodeId: string,
    meta?: { label?: string; value?: string; caption?: string; areas?: string }
  ): Promise<InsightDrawerPayload> {
    const node = taxonomyNode(nodeId);
    const scope = {
      nodes: node,
      node,
      areas: meta?.areas
    };
    const figures = await this.client.get(
      explorerPath('/api/explorer/figures', {
        nodes: scope.nodes,
        areas: scope.areas
      })
    );
    const optional = async (path: string, params: Record<string, string | undefined>) => {
      try {
        const response = await this.client.get(explorerPath(path, params));
        return response.data as unknown;
      } catch {
        return null;
      }
    };
    const [scorecard, players, painPoints, horizon, icpTiers, coverage] =
      await Promise.all([
        optional('/api/explorer/scorecard', {
          nodes: scope.nodes,
          areas: scope.areas
        }),
        optional('/api/explorer/big-players', {
          node: scope.node,
          areas: scope.areas
        }),
        optional('/api/explorer/pain-points', {
          node: scope.node,
          areas: scope.areas,
          sending: DEFAULT_SENDING
        }),
        optional('/api/explorer/horizon', {
          node: scope.node,
          limit: String(HORIZON_PAGE_SIZE),
          offset: '0'
        }),
        optional('/api/explorer/icp-tiers', {
          node: scope.node,
          areas: scope.areas
        }),
        optional('/api/explorer/coverage', {
          node_id: scope.node,
          areas: scope.areas
        })
      ]);
    return drawerFromExplorerParts({
      nodeId,
      label: meta?.label,
      figures: figures.data,
      scorecard,
      players,
      painPoints,
      horizon,
      icpTiers,
      coverage
    });
  }

  async getCompanies(
    query: InsightCompaniesQuery
  ): Promise<InsightCompaniesPageData> {
    const response = await this.client.get(
      explorerPath('/api/explorer/companies', {
        node: taxonomyNode(query.nodeId) ?? query.nodeId,
        areas: query.areaCode,
        customers: query.filter === 'customers' ? 'ours' : undefined,
        sending: query.sending,
        sort: query.sortBy,
        order: query.sortDir,
        page: query.page != null ? String(query.page) : undefined,
        page_size: query.pageSize != null ? String(query.pageSize) : undefined
      })
    );
    return companiesPageFromPayload(response.data, {
      nodeId: query.nodeId,
      title: query.title,
      caption: query.caption
    });
  }

  async createContentPack(
    themeKey: string,
    body: { node: string; sending: string }
  ): Promise<InsightContentPackResult> {
    const response = await this.client.post(
      getInsightsApiPath(
        `/api/explorer/pain-points/${encodeURIComponent(themeKey)}/content`
      ),
      body
    );
    if (response.status === 202) return { status: 'drafting' };
    return { status: 'ready', pack: response.data as InsightContentPack };
  }

  async getContentPack(packId: string): Promise<InsightContentPack> {
    const response = await this.client.get(
      getInsightsApiPath(
        `/api/explorer/content-packs/${encodeURIComponent(packId)}`
      )
    );
    return response.data as InsightContentPack;
  }

  /** A later page of horizon items; the drawer already holds the first. */
  async getHorizon(
    nodeId: string,
    offset: number
  ): Promise<InsightHorizonPage> {
    const response = await this.client.get(
      explorerPath('/api/explorer/horizon', {
        node: taxonomyNode(nodeId),
        limit: String(HORIZON_PAGE_SIZE),
        offset: String(offset)
      })
    );
    return horizonFromPayload(response.data);
  }

  async getDrawerPreference(): Promise<InsightDrawerPreference> {
    const response = await this.client.get(
      getInsightsApiPath('/api/settings/me')
    );
    return { order: drawerOrderFromSettings(response.data) };
  }

  async saveDrawerPreference(
    preference: InsightDrawerPreference
  ): Promise<InsightDrawerPreference> {
    const response = await this.client.put(
      getInsightsApiPath(`/api/settings/me/${DRAWER_ORDER_SETTING_KEY}`),
      drawerOrderToSettingValue(preference.order)
    );
    const saved = (response.data as { value?: unknown } | undefined)?.value;
    return { order: drawerOrderFromSettingValue(saved) };
  }

  async addCompaniesToPipeline(
    payload: InsightBulkPipelineRequest
  ): Promise<InsightBulkPipelineResponse> {
    if (payload.companyIds.length > 20) {
      return {
        status: 'ERROR',
        message: 'Pipelines are capped at 20 companies'
      };
    }
    const crmPipelineId = Number(payload.pipelineId);
    if (!Number.isInteger(crmPipelineId) || crmPipelineId < 1) {
      return {
        status: 'ERROR',
        message: 'Choose a pipeline before adding companies'
      };
    }
    try {
      const response = await this.client.post(
        getInsightsApiPath('/api/explorer/pipeline-adds'),
        {
          crm_pipeline_id: crmPipelineId,
          companies: payload.companyIds,
          source: payload.source ?? 'company_list',
          ...(isPipelineUuid(payload.assigneeUserId)
            ? { assignee_user_uuid: payload.assigneeUserId }
            : {}),
          ...(payload.nodeId || payload.icpTier
            ? {
                context: {
                  ...(payload.nodeId ? { node: payload.nodeId } : {}),
                  ...(payload.icpTier ? { icp_tier: payload.icpTier } : {})
                }
              }
            : {})
        },
        {
          headers: {
            'Idempotency-Key': crypto.randomUUID()
          }
        }
      );
      return pipelineResultFromPayload(response.data);
    } catch (error) {
      return {
        status: 'ERROR',
        message: audienceRequestErrorMessage(
          error,
          'Failed to add companies to pipeline'
        )
      };
    }
  }
}
